import {
  closestCenter, DndContext, pointerWithin, useDroppable, type CollisionDetection, type DragEndEvent, type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Archive, ChevronDown, ChevronRight, Download, GripVertical, History, Inbox, Pencil, Plus, Star, StarOff, Trash2, Upload, UserPlus,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { colorHex, type Section, type Task, type ViewStyle } from "@todoer/shared";
import { Board } from "../components/Board.js";
import { CalendarView } from "../components/CalendarView.js";
import { confirmDialog, Menu, promptDialog } from "../components/overlay.js";
import { ProjectDialog, ShareDialog, VIEW_OPTIONS } from "../components/ProjectDialogs.js";
import { AddTaskButton, CompletedInProject, flatten, reorderCommands, SortableRow, useTaskSensors, type FlatItem } from "../components/TaskList.js";
import { Avatar, Button, Chip, cx, EmptyState, Input, PageHeader, SegmentedControl } from "../components/ui.js";
import { csvToCommands, download, projectToCsv } from "../lib/csv.js";
import { db, useCollaborators, useMemberships, useUser } from "../lib/db.js";
import { cmd, dispatch, uuid } from "../lib/sync.js";
import { showToast } from "../lib/toast.js";

export function ProjectPage({ inbox = false }: { inbox?: boolean }) {
  const params = useParams();
  const navigate = useNavigate();
  const me = useUser();
  const project = useLiveQuery(
    () => (inbox ? db.projects.filter((p) => p.isInbox).first() : db.projects.get(params.id ?? "")),
    [inbox, params.id],
  );
  const id = project?.id ?? "";
  const projects = useLiveQuery(() => db.projects.toArray(), [], []);
  const sections = useLiveQuery(() => db.sections.where("projectId").equals(id).sortBy("sectionOrder"), [id], []);
  const allTasks = useLiveQuery(() => db.tasks.where("projectId").equals(id).toArray(), [id], []);
  const subProjects = projects.filter((p) => p.parentId === id && !p.isArchived);
  const people = useCollaborators();
  const memberships = useMemberships();
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [newSection, setNewSection] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  if (project === undefined) return <div className="h-[34px]" />;
  if (!project) return <EmptyState kind="search" title="Project not found">It may have been deleted, archived or unshared.</EmptyState>;

  const active = allTasks.filter((t) => !t.completedAt);
  const members = memberships.filter((m) => m.projectId === id).map((m) => people.find((p) => p.id === m.userId)).filter((p): p is NonNullable<typeof p> => !!p);
  const isOwner = memberships.some((m) => m.projectId === id && m.userId === me?.id && m.role === "owner");
  const view: ViewStyle = project.viewStyle;
  const setView = (v: ViewStyle) => void dispatch(cmd("project_update", { id, viewStyle: v }));
  const openCount = active.filter((t) => !t.parentId).length;

  const remove = async () => {
    if (!(await confirmDialog({ title: `Delete “${project.name}”?`, body: "Its sub-projects and all their tasks go too. You can restore it from Trash for 30 days.", confirmLabel: "Delete project", danger: true }))) return;
    await dispatch(cmd("project_delete", { id }));
    showToast(`Deleted "${project.name}"`, { label: "Undo", run: () => dispatch(cmd("project_restore", { id })) });
    navigate("/today");
  };
  const archive = async () => {
    await dispatch(cmd("project_update", { id, isArchived: true }));
    showToast(`Archived "${project.name}"`, { label: "Undo", run: () => dispatch(cmd("project_update", { id, isArchived: false })) });
    navigate("/today");
  };
  const exportCsv = async () => {
    const labels = await db.labels.toArray();
    download(`${project.isInbox ? "Inbox" : project.name}.csv`, projectToCsv(active, sections, labels));
  };
  const importCsv = async (file: File) => {
    const labels = await db.labels.toArray();
    const { commands, tasks, sections: s } = csvToCommands(await file.text(), id, labels);
    if (!commands.length) { showToast("That file has no tasks in it"); return; }
    for (let i = 0; i < commands.length; i += 90) await dispatch(...commands.slice(i, i + 90));
    showToast(`Imported ${tasks} task${tasks === 1 ? "" : "s"}${s ? ` and ${s} section${s === 1 ? "" : "s"}` : ""}`);
  };

  return (
    <div>
      <PageHeader
        leading={project.isInbox ? <Inbox size={22} className="shrink-0 text-info" aria-hidden /> : <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full" style={{ background: colorHex(project.color) }} />}
        title={project.isInbox ? "Inbox" : project.name}
        subtitle={`${openCount} open task${openCount === 1 ? "" : "s"}`}
        actions={<>
          {!project.isInbox && (
            <button type="button" onClick={() => setSharing(true)} aria-label={members.length > 1 ? `Shared with ${members.length - 1} people. Manage sharing` : "Share project"} data-tip="Share"
              className="mr-1 flex h-8 items-center gap-1.5 rounded-control px-1.5 text-body text-text-muted hover:bg-hover hover:text-text phone:h-11">
              {members.length > 1
                ? <span className="flex -space-x-1.5">{members.slice(0, 3).map((m) => <Avatar key={m.id} person={m} size={24} className="ring-2 ring-surface" />)}</span>
                : <UserPlus size={18} aria-hidden />}
              <span className="phone:hidden">Share</span>
            </button>
          )}
          <SegmentedControl label="Layout" value={view} onChange={setView} options={VIEW_OPTIONS} size="sm" iconOnlyOnPhone className="phone:hidden" />
          <Menu label="Project actions" items={[
            { label: "Edit project", icon: Pencil, onSelect: () => setEditing(true), hidden: project.isInbox },
            { label: project.isFavorite ? "Remove from favourites" : "Add to favourites", icon: project.isFavorite ? StarOff : Star, onSelect: () => void dispatch(cmd("project_update", { id, isFavorite: !project.isFavorite })), hidden: project.isInbox },
            { label: "Add section", icon: Plus, onSelect: () => { setView("list"); setNewSection(true); } },
            { label: "Export as template (CSV)", icon: Download, divider: true, onSelect: () => void exportCsv() },
            { label: "Import from template (CSV)", icon: Upload, onSelect: () => fileRef.current?.click() },
            { label: "Project activity", icon: History, onSelect: () => navigate(`/activity?project=${id}`) },
            { label: "Archive", icon: Archive, divider: true, onSelect: () => void archive(), hidden: project.isInbox },
            { label: "Delete project", icon: Trash2, onSelect: () => void remove(), danger: true, hidden: project.isInbox || !isOwner },
          ]} />
        </>}
        below={<>
          <SegmentedControl label="Layout" value={view} onChange={setView} options={VIEW_OPTIONS} className="mt-3 flex w-full desk:hidden" />
          {subProjects.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" aria-label="Sub-projects">
              {subProjects.map((p) => <Chip key={p.id} dot={colorHex(p.color)} onClick={() => navigate(`/project/${p.id}`)}>{p.name}</Chip>)}
            </div>
          )}
        </>} />
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void importCsv(f); e.target.value = ""; }} />

      {view === "board" ? (
        <Board projectId={id} sections={sections} tasks={active} allTasks={allTasks} />
      ) : view === "calendar" ? (
        <CalendarView tasks={active.filter((t) => t.due)} projects={projects} projectId={id} />
      ) : (
        <>
          <ListView projectId={id} sections={sections} active={active} allTasks={allTasks} newSection={newSection} setNewSection={setNewSection} />
          {active.length === 0 && !sections.length && (
            <EmptyState compact kind={project.isInbox ? "inbox" : "tasks"} title={project.isInbox ? "Your Inbox is clear" : "No tasks yet"}>
              {project.isInbox ? "Anything you capture without a project lands here." : "Add the first task above, or press Q."}
            </EmptyState>
          )}
          <CompletedInProject projectId={id} />
        </>
      )}

      {editing && <ProjectDialog project={project} onClose={() => setEditing(false)} />}
      {sharing && <ShareDialog project={project} onClose={() => setSharing(false)} />}
    </div>
  );
}

// ------------------------------------------------------------------ list view
const collapsedKey = "todoer.collapsedSections";
function useCollapsedSections() {
  const [set, setSet] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem(collapsedKey) ?? "[]")); } catch { return new Set(); } });
  const toggle = (id: string) => setSet((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    try { localStorage.setItem(collapsedKey, JSON.stringify([...n])); } catch { /* ignore */ }
    return n;
  });
  return [set, toggle] as const;
}

/** Sections first try task rows under the pointer, then the section drop zones. Sections only consider sections. */
const collision: CollisionDetection = (args) => {
  const type = args.active.data.current?.type;
  const isSec = (id: unknown) => String(id).startsWith("sec:");
  if (type === "section") return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => isSec(c.id)) });
  const containers = args.droppableContainers.filter((c) => !isSec(c.id));
  const within = pointerWithin({ ...args, droppableContainers: containers });
  const rows = within.filter((c) => !String(c.id).startsWith("zone:"));
  if (rows.length) return rows;
  if (within.length) return within;
  return closestCenter({ ...args, droppableContainers: containers.filter((c) => !String(c.id).startsWith("zone:")) });
};

function ListView({ projectId, sections, active, allTasks, newSection, setNewSection }: {
  projectId: string; sections: Section[]; active: Task[]; allTasks: Task[]; newSection: boolean; setNewSection: (v: boolean) => void;
}) {
  const sensors = useTaskSensors();
  const [dragging, setDragging] = useState<"task" | "section" | null>(null);
  const [collapsed, toggleCollapsed] = useCollapsedSections();
  const [name, setName] = useState("");
  const groups = useMemo(() => [
    { key: "none", section: null as Section | null, flat: flatten(active.filter((t) => !t.sectionId), allTasks) },
    ...sections.map((s) => ({ key: s.id, section: s, flat: flatten(active.filter((t) => t.sectionId === s.id), allTasks) })),
  ], [sections, active, allTasks]);
  const allFlat = useMemo(() => groups.flatMap((g) => g.flat), [groups]);

  const onDragStart = (e: DragStartEvent) => setDragging(e.active.data.current?.type === "section" ? "section" : "task");
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || overId === activeId) return;
    if (activeId.startsWith("sec:")) {
      const list = [...sections];
      const from = list.findIndex((s) => `sec:${s.id}` === activeId);
      const to = list.findIndex((s) => `sec:${s.id}` === overId);
      if (from < 0 || to < 0) return;
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved!);
      void dispatch(...list.map((s, i) => cmd("section_update", { id: s.id, sectionOrder: i + 1 })));
      return;
    }
    if (overId.startsWith("zone:")) {
      const sectionId = overId === "zone:none" ? null : overId.slice(5);
      const task = active.find((t) => t.id === activeId);
      if (!task || ((task.sectionId ?? null) === sectionId && !task.parentId)) return;
      const end = active.filter((t) => (t.sectionId ?? null) === sectionId && !t.parentId).length + 1;
      void dispatch(cmd("item_move", { id: activeId, sectionId, parentId: null, childOrder: end }));
      return;
    }
    const target = active.find((t) => t.id === overId);
    const c = reorderCommands(active, allFlat, activeId, overId, target?.sectionId ?? null);
    if (c) void dispatch(c);
  };

  const addSection = async () => {
    if (!name.trim()) return;
    await dispatch(cmd("section_add", { id: uuid(), projectId, name: name.trim(), sectionOrder: sections.length + 1 }));
    setName(""); setNewSection(false);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragCancel={() => setDragging(null)} onDragEnd={onDragEnd}>
      <Group zone="none" flat={groups[0]!.flat} projectId={projectId} sectionId={null} dragging={dragging === "task"} />
      <SortableContext items={sections.map((s) => `sec:${s.id}`)} strategy={verticalListSortingStrategy}>
        {groups.slice(1).map((g) => (
          <SectionBlock key={g.key} section={g.section!} flat={g.flat} count={g.flat.filter((f) => f.depth === 0).length} projectId={projectId}
            collapsed={collapsed.has(g.key)} onToggle={() => toggleCollapsed(g.key)} dragging={dragging} />
        ))}
      </SortableContext>
      {newSection ? (
        <form className="mt-8 flex gap-2" onSubmit={(e) => { e.preventDefault(); void addSection(); }}>
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Section name" aria-label="Section name" className="flex-1" />
          <Button type="submit" variant="primary" disabled={!name.trim()}>Add section</Button>
          <Button variant="ghost" onClick={() => setNewSection(false)}>Cancel</Button>
        </form>
      ) : (
        <button type="button" onClick={() => setNewSection(true)}
          className="group mt-6 flex h-10 w-full items-center gap-3 text-body font-medium text-text-faint hover:text-brand-text">
          <span className="h-px flex-1 bg-border group-hover:bg-brand/40" /><span className="flex items-center gap-1.5"><Plus size={16} aria-hidden />Add section</span><span className="h-px flex-1 bg-border group-hover:bg-brand/40" />
        </button>
      )}
    </DndContext>
  );
}

function Group({ zone, flat, projectId, sectionId, dragging, hidden }: { zone: string; flat: FlatItem[]; projectId: string; sectionId: string | null; dragging: boolean; hidden?: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: `zone:${zone}`, data: { type: "zone" } });
  if (hidden) return <div ref={setNodeRef} className={cx("h-2 rounded-control", dragging && "h-10 outline-1 -outline-offset-1 outline-dashed outline-border-strong", isOver && "bg-brand-soft outline-brand")} />;
  return (
    <div ref={setNodeRef} className={cx("rounded-control transition-[background-color,outline-color] duration-150", dragging && "outline-1 outline-offset-2 outline-dashed outline-border-strong", isOver && "bg-brand-soft/60 outline-brand")}>
      <SortableContext id={zone} items={flat.map((f) => f.task.id)} strategy={verticalListSortingStrategy}>
        <ul>{flat.map((f) => <SortableRow key={f.task.id} item={f} />)}</ul>
      </SortableContext>
      <AddTaskButton projectId={projectId} sectionId={sectionId} />
    </div>
  );
}

function SectionBlock({ section, flat, count, projectId, collapsed, onToggle, dragging }: {
  section: Section; flat: FlatItem[]; count: number; projectId: string; collapsed: boolean; onToggle: () => void; dragging: "task" | "section" | null;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: `sec:${section.id}`, data: { type: "section" } });
  const rename = async () => { const n = await promptDialog({ title: "Rename section", label: "Section name", value: section.name }); if (n) void dispatch(cmd("section_update", { id: section.id, name: n })); };
  const remove = async () => {
    if (await confirmDialog({ title: `Delete “${section.name}”?`, body: "The section and its tasks move to Trash.", confirmLabel: "Delete", danger: true })) void dispatch(cmd("section_delete", { id: section.id }));
  };
  return (
    <section ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} aria-label={section.name}
      className={cx("mt-8 rounded-control", isDragging && "relative z-20 bg-surface-raised opacity-90 shadow-pop")}>
      <div className="group/sec flex min-h-9 items-center gap-1 border-b border-border pb-1">
        <button ref={setActivatorNodeRef} type="button" {...attributes} {...listeners} aria-label={`Reorder section ${section.name}`} data-tip="Drag to reorder"
          className="-ml-7 grid h-7 w-6 cursor-grab touch-none place-items-center rounded text-text-faint opacity-0 hover:text-text focus-visible:opacity-100 group-hover/sec:opacity-100 phone:hidden active:cursor-grabbing">
          <GripVertical size={16} aria-hidden />
        </button>
        <button type="button" onClick={onToggle} aria-expanded={!collapsed} aria-label={`${collapsed ? "Expand" : "Collapse"} ${section.name}`}
          className="grid h-7 w-7 place-items-center rounded text-text-faint hover:bg-hover hover:text-text phone:h-11 phone:w-8">
          {collapsed ? <ChevronRight size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
        </button>
        <h2 className="min-w-0 truncate text-heading font-semibold" onDoubleClick={() => void rename()}>{section.name}</h2>
        {count > 0 && <span className="tabular text-meta text-text-faint">{count}</span>}
        <span className="ml-auto opacity-0 transition-opacity focus-within:opacity-100 group-hover/sec:opacity-100 phone:opacity-100">
          <Menu size="sm" label={`${section.name} actions`} items={[
            { label: "Rename section", icon: Pencil, onSelect: () => void rename() },
            { label: "Delete section", icon: Trash2, danger: true, divider: true, onSelect: () => void remove() },
          ]} />
        </span>
      </div>
      {!collapsed || dragging === "task"
        ? <Group zone={section.id} flat={collapsed ? [] : flat} projectId={projectId} sectionId={section.id} dragging={dragging === "task"} hidden={collapsed} />
        : null}
    </section>
  );
}
